import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Eye, EyeOff, Lock, Mail, User } from 'lucide-react';

import { useApi } from '../../../shared/api/api-context.js';
import { PhoneField } from '../../../shared/components/phone-field.js';
import { PHONE_CODES } from '../../../shared/constants/phone-codes.js';
import { composeInternationalPhone, DEFAULT_DIAL_CODE } from '../../../shared/phone.js';
import { useSite } from '../../../shared/site-context.js';
import { useTranslation } from '../../../shared/use-translation.js';
import { guestProfileQueryKey, type GuestProfileDetails } from '../../booking/api/booking-api.js';
import { useBooking } from '../../booking/state/booking-context.js';
import { ProviderButtons } from './provider-buttons.js';

const MIN_PASSWORD_LENGTH = 12;
const MIN_MOBILE_DIGITS = 8;
// The dial prefixes the phone select offers — "+61-AU" style values carry
// the prefix in front of the dash.
const PHONE_DIAL_CODES = [
  ...new Set(PHONE_CODES.map((code) => code.value.split('-')[0] ?? '')),
].filter((code) => code !== '');

interface RegisterResponse {
  readonly accessToken: string;
  readonly guestId: string;
  readonly email: string;
}

interface GoogleSignInResponse {
  readonly accessToken: string;
  readonly guestId: string;
  readonly email: string;
}

export interface SignUpInitialValues {
  readonly firstName?: string;
  readonly lastName?: string;
  readonly email?: string;
  readonly countryCode?: string;
  readonly mobile?: string;
  readonly marketingConsent?: boolean;
}

// Guest registration form shared by the sign-up page and the in-context
// SignUpDialog — onSuccess decides what happens next (navigate or close).
// `initialValues` pre-fills the identity fields from a form the guest already
// completed (checkout's "Join free"); prefilled fields are read-only so the
// dialog only asks for a password and the two consents.
export function SignUpForm({
  onSuccess,
  initialValues,
}: {
  readonly onSuccess: () => void;
  readonly initialValues?: SignUpInitialValues | undefined;
}): React.JSX.Element {
  const { t } = useTranslation();
  const api = useApi();
  const booking = useBooking();
  const site = useSite();
  const queryClient = useQueryClient();

  const [firstName, setFirstName] = useState(initialValues?.firstName ?? '');
  const [lastName, setLastName] = useState(initialValues?.lastName ?? '');
  const [email, setEmail] = useState(initialValues?.email ?? '');
  const [countryCode, setCountryCode] = useState(initialValues?.countryCode ?? DEFAULT_DIAL_CODE);
  const [mobile, setMobile] = useState(initialValues?.mobile ?? '');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [agreeTerms, setAgreeTerms] = useState(false);
  const [marketingConsent, setMarketingConsent] = useState(
    initialValues?.marketingConsent ?? false,
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const prefilled = (value: string | undefined): boolean =>
    value !== undefined && value.trim() !== '';
  // Field errors are submit-set, so clear each one as soon as the guest
  // corrects that field — otherwise a fixed input still shows its stale error
  // until the next submit.
  const clearFieldError = (field: string): void => {
    setFieldErrors((prev) =>
      Object.fromEntries(Object.entries(prev).filter(([key]) => key !== field)),
    );
  };

  const validate = (): boolean => {
    const errors: Record<string, string> = {};
    if (firstName.trim().length === 0) errors.firstName = t('auth.firstNameRequired');
    if (lastName.trim().length === 0) errors.lastName = t('auth.lastNameRequired');
    if (email.trim().length === 0) {
      errors.email = t('auth.emailRequired');
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      errors.email = t('auth.emailInvalid');
    }
    const mobileDigits = mobile.replace(/\D/g, '');
    if (mobileDigits.length === 0) {
      errors.mobile = t('auth.mobileRequired');
    } else if (mobileDigits.length < MIN_MOBILE_DIGITS) {
      errors.mobile = t('auth.mobileInvalid');
    }
    if (password.length === 0) {
      errors.password = t('auth.passwordRequired');
    } else if (password.length < MIN_PASSWORD_LENGTH) {
      errors.password = t('auth.passwordMinLength', { min: String(MIN_PASSWORD_LENGTH) });
    } else if (!/(?=.*[A-Za-z])(?=.*\d)/.test(password)) {
      errors.password = t('auth.passwordPattern');
    }
    if (!agreeTerms) {
      errors.agreeTerms = t('auth.termsRequired');
    }
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (event: React.SyntheticEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    if (!validate()) return;
    setLoading(true);
    setError(null);
    try {
      // A mobile typed as the whole international number — "+91…", "0091…",
      // or digits too long for a national number beginning with a known
      // prefix — keeps its own prefix rather than getting the selected one
      // prepended a second time.
      const phone = composeInternationalPhone(mobile, countryCode, PHONE_DIAL_CODES, ' ');
      const result = await api.post<RegisterResponse>('/v1/auth/guest/register', {
        email: email.trim().toLowerCase(),
        password,
      });

      api.setAccessToken(result.accessToken);
      booking.setGuestAuth(result.accessToken, result.guestId);

      await api.patch<unknown>(`/v1/guest/profile/${result.guestId}`, {
        email: email.trim().toLowerCase(),
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        phone,
      });

      await api.post<unknown>(`/v1/guest/profile/${result.guestId}/marketing-consent`, {
        marketingConsent,
      });

      // The PATCH above already wrote this profile — seeding it means the
      // checkout prefill reads the cache instead of fetching it back.
      queryClient.setQueryData(guestProfileQueryKey(result.guestId), {
        guestId: result.guestId,
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        email: email.trim().toLowerCase(),
        phone,
      } satisfies GuestProfileDetails);

      onSuccess();
    } catch (err) {
      const message = err instanceof Error ? err.message : t('auth.signUpError');
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogle = async (credential: string): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const result = await api.post<GoogleSignInResponse>('/v1/auth/guest/google', {
        idToken: credential,
      });
      api.setAccessToken(result.accessToken);
      booking.setGuestAuth(result.accessToken, result.guestId);
      onSuccess();
    } catch (err) {
      const message = err instanceof Error ? err.message : t('auth.googleError');
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <form onSubmit={(e) => void handleSubmit(e)} className="space-y-4">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <label htmlFor="signup-firstname" className="ibe-label">
              {t('auth.firstName')}
            </label>
            <div className="relative">
              <User className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <input
                id="signup-firstname"
                type="text"
                value={firstName}
                onChange={(e) => {
                  setFirstName(e.target.value);
                  clearFieldError('firstName');
                }}
                placeholder={t('auth.firstNamePlaceholder')}
                className="ibe-control pl-10"
                aria-invalid={fieldErrors.firstName !== undefined}
                disabled={loading}
                readOnly={prefilled(initialValues?.firstName)}
              />
            </div>
            {fieldErrors.firstName !== undefined && (
              <p className="ibe-error">{fieldErrors.firstName}</p>
            )}
          </div>
          <div>
            <label htmlFor="signup-lastname" className="ibe-label">
              {t('auth.lastName')}
            </label>
            <div className="relative">
              <User className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <input
                id="signup-lastname"
                type="text"
                value={lastName}
                onChange={(e) => {
                  setLastName(e.target.value);
                  clearFieldError('lastName');
                }}
                placeholder={t('auth.lastNamePlaceholder')}
                className="ibe-control pl-10"
                aria-invalid={fieldErrors.lastName !== undefined}
                disabled={loading}
                readOnly={prefilled(initialValues?.lastName)}
              />
            </div>
            {fieldErrors.lastName !== undefined && (
              <p className="ibe-error">{fieldErrors.lastName}</p>
            )}
          </div>
        </div>

        <div>
          <label htmlFor="signup-email" className="ibe-label">
            {t('auth.email')}
          </label>
          <div className="relative">
            <Mail className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <input
              id="signup-email"
              type="email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                clearFieldError('email');
              }}
              placeholder={t('auth.emailPlaceholder')}
              className="ibe-control pl-10"
              aria-invalid={fieldErrors.email !== undefined}
              disabled={loading}
              readOnly={prefilled(initialValues?.email)}
            />
          </div>
          {fieldErrors.email !== undefined && <p className="ibe-error">{fieldErrors.email}</p>}
        </div>

        <PhoneField
          id="signup-mobile"
          label={t('auth.mobile')}
          countryCode={countryCode}
          value={mobile}
          onCountryChange={setCountryCode}
          onChange={(value) => {
            setMobile(value);
            clearFieldError('mobile');
          }}
          error={fieldErrors.mobile}
          disabled={loading || prefilled(initialValues?.mobile)}
          placeholder={t('auth.mobilePlaceholder')}
        />

        <div>
          <label htmlFor="signup-password" className="ibe-label">
            {t('auth.password')}
          </label>
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <input
              id="signup-password"
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                clearFieldError('password');
              }}
              placeholder={t('auth.createPasswordPlaceholder')}
              className="ibe-control pl-10 pr-10"
              aria-invalid={fieldErrors.password !== undefined}
              disabled={loading}
            />
            <button
              type="button"
              onClick={() => {
                setShowPassword((s) => !s);
              }}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              aria-label={showPassword ? t('auth.hidePassword') : t('auth.showPassword')}
              tabIndex={-1}
            >
              {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
          {fieldErrors.password !== undefined && (
            <p className="ibe-error">{fieldErrors.password}</p>
          )}
          <p className="mt-1 text-xs text-muted-foreground">
            {t('auth.passwordHint', { min: String(MIN_PASSWORD_LENGTH) })}
          </p>
        </div>

        <div className="space-y-3 pt-2">
          <label className="inline-flex items-start gap-2 text-sm text-foreground">
            <input
              type="checkbox"
              checked={agreeTerms}
              onChange={(e) => {
                setAgreeTerms(e.target.checked);
                if (e.target.checked) clearFieldError('agreeTerms');
              }}
              className="h-4 w-4 accent-primary mt-0.5"
              aria-invalid={fieldErrors.agreeTerms !== undefined}
              disabled={loading}
            />
            <span>
              {t('auth.termsAgree')}{' '}
              <Link to="/" className="font-medium text-on-primary-container hover:underline">
                {t('guest.terms')}
              </Link>{' '}
              {t('guest.termsAnd')}{' '}
              <Link to="/" className="font-medium text-on-primary-container hover:underline">
                {t('guest.privacy')}
              </Link>
            </span>
          </label>
          {fieldErrors.agreeTerms !== undefined && (
            <p className="ibe-error">{fieldErrors.agreeTerms}</p>
          )}

          <label className="inline-flex items-start gap-2 text-sm text-foreground">
            <input
              type="checkbox"
              checked={marketingConsent}
              onChange={(e) => {
                setMarketingConsent(e.target.checked);
              }}
              className="h-4 w-4 accent-primary mt-0.5"
              disabled={loading}
            />
            <span>{t('auth.marketingConsent', { site: site.name })}</span>
          </label>
        </div>

        <button type="submit" disabled={loading} className="ibe-primary-button w-full">
          {loading ? t('auth.creatingAccount') : t('auth.createAccountButton')}
        </button>

        {error !== null && (
          <p className="ibe-error text-center" role="alert">
            {error}
          </p>
        )}
      </form>

      <div className="mt-6">
        <ProviderButtons
          onGoogleCredential={(credential) => {
            void handleGoogle(credential);
          }}
          disabled={loading}
        />
      </div>
    </>
  );
}
